import {
  useEffect,
  useMemo,
  useState
} from 'react';

import MultiFileSubmissionControls
  from '../../student/course/MultiFileSubmissionControls';

import {
  fetchSubmissionFileSlots,
  getSubmissionFileExtension,
  normalizeSubmissionFileSettings,
  safeSubmissionFilePart
} from '../../../utils/assignmentSubmissionFiles';


const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const API_URL =
  process.env.REACT_APP_API_URL;


const headers =
  extra => ({
    apikey:
      SUPABASE_KEY,

    Authorization:
      'Bearer ' +
      SUPABASE_KEY,

    ...extra
  });


async function json(
  response,
  label
) {
  /*
   * Some callers pass fetch() directly.
   * Accept either the Response itself or
   * the Promise returned by fetch().
   */
  response =
    await response;

  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      label +
        ': ' +
        (
          text ||
          response.statusText
        )
    );
  }

  return text
    ? JSON.parse(text)
    : null;
}


function mime(
  name
) {
  return /\.pdf$/i.test(
    String(name || '')
  )
    ? 'application/pdf'
    : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
}


function base64(
  file
) {
  return new Promise(
    (
      resolve,
      reject
    ) => {
      const reader =
        new FileReader();

      reader.onload =
        event => {
          const value =
            String(
              event.target
                ?.result ||
              ''
            );

          resolve(
            value.includes(',')
              ? value.split(',')[1]
              : value
          );
        };

      reader.onerror =
        () =>
          reject(
            new Error(
              'Could not read the selected file from your device.'
            )
          );

      reader.readAsDataURL(
        file
      );
    }
  );
}


async function patchSubmission(
  id,
  data
) {
  return json(
    await fetch(
      SUPABASE_URL +
        '/rest/v1/assignment_submissions?id=eq.' +
        encodeURIComponent(
          id
        ),
      {
        method:
          'PATCH',

        headers:
          headers({
            'Content-Type':
              'application/json',

            Prefer:
              'return=representation'
          }),

        body:
          JSON.stringify(
            data
          )
      }
    ),
    'Saving submission'
  );
}


export default function InstructorAssignmentUpload({
  courseId,
  courseAssignment,
  target,
  student,
  submission,
  currentInstructorId,
  onSubmitted
}) {
  const [
    open,
    setOpen
  ] =
    useState(false);

  const [
    setting,
    setSetting
  ] =
    useState({});

  const [
    slots,
    setSlots
  ] =
    useState([]);

  const [
    file,
    setFile
  ] =
    useState(null);

  const [
    busy,
    setBusy
  ] =
    useState(false);

  const [
    error,
    setError
  ] =
    useState('');


  const typeId =
    courseAssignment
      ?.assignment_type_id;

  const kind =
    courseAssignment
      ?.assignment_types
      ?.assignment_kind ||
    'standard';

  const config =
    useMemo(
      () =>
        normalizeSubmissionFileSettings(
          setting
        ),
      [setting]
    );


  useEffect(() => {
    setOpen(false);
    setFile(null);
    setError('');
  }, [
    target?.enrolmentId,
    target?.assignmentId,
    target?.scheduleItemId
  ]);


  useEffect(() => {
    if (
      !open ||
      !typeId
    ) {
      return;
    }

    let cancelled =
      false;

    Promise.all([
      json(
        fetch(
          SUPABASE_URL +
            '/rest/v1/course_assignment_type_settings?course_id=eq.' +
            encodeURIComponent(
              courseId
            ) +
            '&assignment_type_id=eq.' +
            encodeURIComponent(
              typeId
            ) +
            '&select=*&limit=1',
          {
            headers:
              headers()
          }
        ),
        'Loading settings'
      ),

      fetchSubmissionFileSlots(
        courseId
      )
    ])
      .then(
        (
          [
            rows,
            allSlots
          ]
        ) => {
          if (cancelled) {
            return;
          }

          setSetting(
            (
              Array.isArray(rows)
                ? rows[0]
                : null
            ) ||
            {}
          );

          setSlots(
            (
              allSlots ||
              []
            ).filter(
              slot =>
                String(
                  slot
                    .assignment_type_id
                ) ===
                String(
                  typeId
                )
            )
          );
        }
      )
      .catch(
        err => {
          if (!cancelled) {
            setError(
              err.message
            );
          }
        }
      );

    return () => {
      cancelled =
        true;
    };
  }, [
    open,
    courseId,
    typeId
  ]);


  if (
    kind !==
      'standard' ||
    target
      ?.structuredFormInstanceId
  ) {
    return null;
  }


  const label =
    String(
      courseAssignment
        ?.instance_name ||
      setting
        ?.display_name ||
      courseAssignment
        ?.assignment_types
        ?.name ||
      'Assignment'
    ).trim();


  const owner =
    target
      ?.assignmentGroupNumber
      ? (
          'Group ' +
          target
            .assignmentGroupNumber
        )
      : String(
          student
            ?.student_code ||
          'Student'
        );


  function storedName(
    selected,
    slot
  ) {
    const ext =
      getSubmissionFileExtension(
        selected
      ) ||
      'file';

    return (
      safeSubmissionFilePart(
        label
      ) +
      (
        slot
          ? (
              ' - ' +
              safeSubmissionFilePart(
                slot.label
              )
            )
          : ''
      ) +
      ' - ' +
      safeSubmissionFilePart(
        owner
      ) +
      '.' +
      ext
    );
  }


  async function ensureSubmission() {
    if (submission?.id) {
      return submission;
    }

    const schedule =
      target.scheduleItemId
        ? (
            'eq.' +
            encodeURIComponent(
              target
                .scheduleItemId
            )
          )
        : 'is.null';

    const existing =
      await json(
        await fetch(
          SUPABASE_URL +
            '/rest/v1/assignment_submissions?enrolment_id=eq.' +
            encodeURIComponent(
              target
                .enrolmentId
            ) +
            '&course_assignment_id=eq.' +
            encodeURIComponent(
              courseAssignment.id
            ) +
            '&course_assignment_schedule_item_id=' +
            schedule +
            '&structured_form_instance_id=is.null&select=*&limit=1',
          {
            headers:
              headers()
          }
        ),
        'Checking submission'
      );

    if (
      existing?.[0]
    ) {
      return existing[0];
    }

    const created =
      await json(
        await fetch(
          SUPABASE_URL +
            '/rest/v1/assignment_submissions',
          {
            method:
              'POST',

            headers:
              headers({
                'Content-Type':
                  'application/json',

                Prefer:
                  'return=representation'
              }),

            body:
              JSON.stringify({
                enrolment_id:
                  target
                    .enrolmentId,

                course_assignment_id:
                  courseAssignment
                    .id,

                course_assignment_schedule_item_id:
                  target
                    .scheduleItemId ||
                  null,

                submitted:
                  false
              })
          }
        ),
        'Creating submission'
      );

    if (
      !created?.[0]
    ) {
      throw new Error(
        'Could not create the submission record.'
      );
    }

    return created[0];
  }


  async function uploadSingle() {
    if (
      !file ||
      !currentInstructorId
    ) {
      return;
    }

    setBusy(true);
    setError('');

    let row =
      null;

    let previous =
      null;

    try {
      row =
        await ensureSubmission();

      previous = {
        submitted:
          row.submitted,

        submitted_at:
          row.submitted_at,

        drive_file_id:
          row.drive_file_id,

        file_name:
          row.file_name,

        review_pdf_drive_file_id:
          row
            .review_pdf_drive_file_id,

        review_pdf_file_name:
          row
            .review_pdf_file_name,

        file_source_submission_id:
          row
            .file_source_submission_id,

        submitted_by_instructor_id:
          row
            .submitted_by_instructor_id
      };


      const clearedRows = await patchSubmission(
        row.id,
        {
          submitted:
            false,

          submitted_at:
            null,

          drive_file_id:
            null,

          file_name:
            null,

          review_pdf_drive_file_id:
            null,

          review_pdf_file_name:
            null,

          file_source_submission_id:
            null
        }
      );

      if (!clearedRows?.[0] || clearedRows[0].drive_file_id) {
        throw new Error('Could not prepare the replacement upload.');
      }


      const fileName =
        storedName(
          file
        );


      await fetch(
        API_URL,
        {
          method:
            'POST',

          mode:
            'no-cors',

          headers: {
            'Content-Type':
              'application/json'
          },

          body:
            JSON.stringify({
              action:
                'uploadAssignmentSubmission',

              submissionId:
                row.id,

              courseId,

              fileName,

              base64Data:
                await base64(
                  file
                ),

              /*
               * Deliberately derive the MIME from the filename.
               * Do not inspect or trust browser MIME metadata.
               */
              mimeType:
                mime(
                  file.name
                )
            })
        }
      );


      let uploaded =
        null;

      for (
        let i = 0;
        i < 30 &&
        !uploaded;
        i += 1
      ) {
        await new Promise(
          resolve =>
            setTimeout(
              resolve,
              2000
            )
        );

        const check =
          await json(
            await fetch(
              SUPABASE_URL +
                '/rest/v1/assignment_submissions?id=eq.' +
                encodeURIComponent(
                  row.id
                ) +
                '&select=*&limit=1',
              {
                headers:
                  headers()
              }
            ),
            'Checking upload'
          );

        if (
          check?.[0]
            ?.drive_file_id
        ) {
          uploaded =
            check[0];
        }
      }


      if (!uploaded) {
        throw new Error(
          'The file upload did not complete.'
        );
      }


      const finalRows =
        await patchSubmission(
          row.id,
          {
            submitted:
              true,

            submitted_at:
              new Date()
                .toISOString(),

            drive_file_id:
              uploaded
                .drive_file_id,

            file_name:
              fileName,

            review_pdf_drive_file_id:
              uploaded
                .review_pdf_drive_file_id ||
              null,

            review_pdf_file_name:
              uploaded
                .review_pdf_file_name ||
              null,

            submitted_by_instructor_id:
              currentInstructorId
          }
        );


      if (
        !finalRows?.[0]
      ) {
        throw new Error(
          'The upload could not be finalized.'
        );
      }


      setFile(null);

      if (onSubmitted) {
        await onSubmitted(
          finalRows[0]
        );
      }

    } catch (err) {
      if (
        row?.id &&
        previous
      ) {
        try {
          await patchSubmission(
            row.id,
            previous
          );

        } catch (
          restoreErr
        ) {
          console.error(
            'Could not restore previous submission:',
            restoreErr
          );
        }
      }

      setError(
        err.message ||
        'Upload failed.'
      );

    } finally {
      setBusy(false);
    }
  }


  return (
    <div
      style={{
        marginTop:
          14
      }}
    >
      {!open ? (
        <button
          type="button"
          disabled={
            !currentInstructorId
          }
          onClick={() =>
            setOpen(true)
          }
          style={{
            padding:
              '7px 11px',

            border:
              '1px solid #9fb7c7',

            borderRadius:
              7,

            background:
              'white',

            color:
              '#215b7d',

            fontWeight:
              800
          }}
        >
          {submission?.submitted
            ? 'Replace file on behalf of student'
            : 'Upload on behalf of student'}
        </button>

      ) : (
        <div
          style={{
            marginTop:
              8,

            padding:
              12,

            border:
              '1px solid #cbd5df',

            borderRadius:
              8,

            background:
              '#f8fafb'
          }}
        >
          <div
            style={{
              fontWeight:
                800,

              marginBottom:
                5
            }}
          >
            Upload on behalf of student
          </div>

          <div
            style={{
              fontSize:
                12,

              color:
                '#66788a',

              marginBottom:
                10
            }}
          >
            Instructor override:
            deadlines do not apply and
            PDFs are not inspected or
            validated.
          </div>

          {error && (
            <div
              style={{
                color:
                  '#a12a2a',

                marginBottom:
                  8
              }}
            >
              {error}
            </div>
          )}


          {config.multiFile ? (
            <MultiFileSubmissionControls
              submission={
                submission
              }
              slots={
                slots
              }
              courseId={
                courseId
              }
              status="open"
              ensureSubmission={
                ensureSubmission
              }
              allowSubmittedEditing
              skipFileValidation
              allowPdfImages
              buildStoredFileName={
                (
                  slot,
                  selected
                ) =>
                  storedName(
                    selected,
                    slot
                  )
              }
              getFinalizeData={() => ({
                submitted_by_instructor_id:
                  currentInstructorId
              })}
              onSubmitted={
                onSubmitted
              }
            />

          ) : (
            <>
              <input
                type="file"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                disabled={
                  busy
                }
                onChange={
                  event => {
                    const selected =
                      event
                        .target
                        .files?.[0] ||
                      null;

                    const ext =
                      getSubmissionFileExtension(
                        selected
                      );

                    /*
                     * This is only an identification check:
                     * a file named .pdf is accepted as a PDF.
                     * No parsing or content validation occurs.
                     */
                    if (
                      selected &&
                      ext !==
                        'pdf' &&
                      ext !==
                        'docx'
                    ) {
                      setFile(null);

                      setError(
                        'Choose a PDF or Word (.docx) file.'
                      );

                    } else {
                      setFile(
                        selected
                      );

                      setError('');
                    }
                  }
                }
              />

              <button
                type="button"
                disabled={
                  busy ||
                  !file
                }
                onClick={
                  uploadSingle
                }
                style={{
                  display:
                    'block',

                  marginTop:
                    10
                }}
              >
                {busy
                  ? 'Uploading...'
                  : 'Submit for student'}
              </button>
            </>
          )}


          <button
            type="button"
            disabled={
              busy
            }
            onClick={() =>
              setOpen(false)
            }
            style={{
              marginTop:
                10
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
